import { useState, useEffect, useRef, useCallback } from 'react'
import { Monitor, RefreshCw, Square, Cpu, ScreenShare, X } from 'lucide-react'
import { useEventStore } from '@/store'
import DevicePanel, { type DeviceStats } from '@/admin/components/DevicePanel'
import RemoteControlViewer, { type RcInboundPayload } from '@/admin/components/RemoteControlViewer'

type BoothStatus = {
  screen: string
  currentPose: number
  mode: string
  timestamp: number
  eventId: string
  appVersion?: string | null
}

type DeviceEntry = {
  deviceId: string
  stats: DeviceStats | null
  online: boolean
}

const REQUEST_TIMEOUT_MS = 130000

export default function GlobalMonitor() {
  const events = useEventStore((s) => s.events)
  const loadEvents = useEventStore((s) => s.loadEvents)

  useEffect(() => {
    loadEvents().catch(console.error)
  }, [loadEvents])

  const [connected, setConnected] = useState(false)
  const [activeBooths, setActiveBooths] = useState<Record<string, BoothStatus>>({})
  const [devices, setDevices] = useState<Record<string, DeviceEntry>>({})
  const [openDevice, setOpenDevice] = useState<string | null>(null)
  const wsRef = useRef<WebSocket | null>(null)

  // Active remote-control session, if any. Only the signalling state lives
  // here, the WebRTC connection itself belongs to RemoteControlViewer, which
  // registers its inbound handler on this ref.
  const [rc, setRc] = useState<{ deviceId: string; sessionId: string; iceServers: RTCIceServer[]; unattended: boolean } | null>(null)
  const [rcPending, setRcPending] = useState<string | null>(null)
  const [rcError, setRcError] = useState<string | null>(null)
  const rcInboundRef = useRef<((payload: RcInboundPayload) => void) | null>(null)
  // "Take over" button starts in this mode until changed, persisted like any
  // other per-admin UI preference, read fresh at click time so it isn't stale.
  const [rcUnattendedDefault, setRcUnattendedDefault] = useState(() => {
    try { return localStorage.getItem('posebox:rcUnattendedDefault') === '1' } catch { return false }
  })
  const toggleRcUnattendedDefault = () => {
    setRcUnattendedDefault((prev) => {
      const next = !prev
      try { localStorage.setItem('posebox:rcUnattendedDefault', next ? '1' : '0') } catch { /* best effort */ }
      return next
    })
  }
  // `connect`'s onmessage closure is memoized once, so it can't read fresh
  // state; a ref sidesteps that for the one value it needs from this click.
  const rcPendingUnattendedRef = useRef(false)

  /**
   * In-flight device requests, keyed by requestId. The socket is a single
   * multiplexed channel, so replies have to be matched back to the caller that
   * is awaiting them.
   */
  const pending = useRef(new Map<string, { resolve: (v: any) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }>())

  const connect = useCallback(() => {
    if (wsRef.current) wsRef.current.close()

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(`${protocol}//${window.location.host}?eventId=GLOBAL_ADMIN`)

    ws.onopen = () => {
      setConnected(true)
      // Devices connect independently of booth pages, so ask for the current
      // roster rather than waiting for one to announce itself.
      ws.send(JSON.stringify({ type: 'DEVICE_LIST' }))
    }
    ws.onclose = () => setConnected(false)
    ws.onmessage = async (e) => {
      try {
        let text = ''
        if (e.data instanceof Blob) {
          text = await e.data.text()
        } else {
          text = e.data
        }
        const message = JSON.parse(text)

        if (message.type === 'BOOTH_STATUS') {
          // Key by deviceId. If a device hops events, it will update its eventId here
          setActiveBooths(prev => ({
            ...prev,
            [message.deviceId]: {
              screen: message.screen,
              currentPose: message.currentPose,
              mode: message.mode,
              timestamp: message.timestamp,
              eventId: message.eventId,
              appVersion: message.appVersion ?? null,
            }
          }))
          return
        }

        if (message.type === 'DEVICE_LIST') {
          const next: Record<string, DeviceEntry> = {}
          for (const d of message.devices || []) {
            next[d.deviceId] = { deviceId: d.deviceId, stats: d.stats, online: true }
          }
          setDevices(next)
          return
        }

        if (message.type === 'DEVICE_PRESENCE') {
          setDevices(prev => ({
            ...prev,
            [message.deviceId]: {
              deviceId: message.deviceId,
              stats: prev[message.deviceId]?.stats ?? null,
              online: !!message.online,
            }
          }))
          return
        }

        if (message.type === 'DEVICE_STATS') {
          setDevices(prev => ({
            ...prev,
            [message.deviceId]: { deviceId: message.deviceId, stats: message.stats, online: true }
          }))
          return
        }

        if (message.type === 'RC_STARTED') {
          setRcPending(null)
          // The server doesn't echo the mode back; rcPendingUnattended was
          // set to what this click actually asked for just before sending.
          setRc({ deviceId: message.deviceId, sessionId: message.sessionId, iceServers: message.iceServers || [], unattended: rcPendingUnattendedRef.current })
          return
        }

        if (message.type === 'RC_SIGNAL') {
          rcInboundRef.current?.(message.payload)
          return
        }

        if (message.type === 'RC_ENDED') {
          setRcPending(null)
          setRcError(message.reason === 'Device is already being controlled'
            ? 'This device is already being controlled from another session.'
            : message.reason === 'Device is not connected'
              ? 'Device is not connected.'
              : null)
          setRc((cur) => (cur && cur.sessionId === message.sessionId ? null : cur))
          return
        }

        if (message.type === 'DEVICE_RESPONSE') {
          const entry = pending.current.get(message.requestId)
          if (!entry) return
          pending.current.delete(message.requestId)
          clearTimeout(entry.timer)
          if (message.ok) entry.resolve(message.data)
          else entry.reject(new Error(message.error || 'Device request failed'))
        }
      } catch (err) {
        console.error('Failed to parse WS message in global monitor', err)
      }
    }
    wsRef.current = ws
  }, [])

  /**
   * Ask a device to do something and await its answer.
   *
   * The timeout is slightly longer than the server's own, so a request that
   * dies on the server surfaces the server's specific error rather than this
   * generic one.
   */
  const request = useCallback((deviceId: string, op: string, params: Record<string, unknown> = {}) => {
    return new Promise<any>((resolve, reject) => {
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        reject(new Error('Not connected to the server'))
        return
      }
      const requestId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`
      const timer = setTimeout(() => {
        pending.current.delete(requestId)
        reject(new Error('The device did not respond'))
      }, REQUEST_TIMEOUT_MS)
      pending.current.set(requestId, { resolve, reject, timer })
      ws.send(JSON.stringify({ type: 'DEVICE_REQUEST', deviceId, requestId, op, params }))
    })
  }, [])

  useEffect(() => {
    connect()

    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.CLOSED) {
        console.log('WS closed, reconnecting global monitor...')
        connect()
      }
      
      setActiveBooths(prev => {
        const now = Date.now()
        let changed = false
        const next = { ...prev }
        for (const [id, data] of Object.entries(next)) {
          if (now - data.timestamp > 15000) {
            delete next[id]
            changed = true
          }
        }
        return changed ? next : prev
      })
    }, 5000)

    return () => {
      if (wsRef.current) wsRef.current.close()
      clearInterval(interval)
    }
  }, [connect])

  const sendCommand = (type: string, data = {}) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    wsRef.current.send(JSON.stringify({ type, ...data }))
  }

  const startRemoteControl = (deviceId: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    setRcError(null)
    setRcPending(deviceId)
    rcPendingUnattendedRef.current = rcUnattendedDefault
    wsRef.current.send(JSON.stringify({
      type: 'RC_START',
      deviceId,
      lock: !rcUnattendedDefault,
      viewOnly: rcUnattendedDefault,
    }))
  }

  const sendRcSignal = useCallback((payload: Record<string, unknown>) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    // Ending a session is a dedicated message so the server closes its audit
    // row immediately, rather than waiting on a round trip to the device.
    const body = payload as { sessionId: string; payload?: { kind?: string } }
    if (body.payload?.kind === 'stop') {
      wsRef.current.send(JSON.stringify({ type: 'RC_STOP', sessionId: body.sessionId }))
      return
    }
    wsRef.current.send(JSON.stringify({ type: 'RC_SIGNAL', ...payload }))
  }, [])

  const closeRemoteControl = useCallback(() => {
    setRc(null)
    setRcPending(null)
  }, [])

  // Agents whose machine has no booth page reporting. Listed separately rather
  // than hidden, an unreachable-looking kiosk that is actually fine is exactly
  // what this view is for.
  const unattachedDevices = Object.values(devices).filter(
    (d) => d.online && !activeBooths[d.deviceId]
  )

  // Group active booths by event
  const groupedBooths = Object.entries(activeBooths).reduce((acc, [deviceId, status]) => {
    if (!acc[status.eventId]) acc[status.eventId] = []
    acc[status.eventId].push({ deviceId, ...status })
    return acc
  }, {} as Record<string, Array<BoothStatus & { deviceId: string }>>)

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6 bg-background min-h-screen text-foreground">
      <div className="flex items-center justify-between border-b pb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Monitor className="text-primary" /> Live Booths Monitor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time overview of all active photobooth sessions across all events.</p>
        </div>
        <div className="flex items-center gap-4">
          <label
            className="flex cursor-pointer items-center gap-2 text-sm"
            title="When on, Take Over opens in unattended view by default: no overlay on the kiosk, no input, watch-only, for checking a live event without interrupting it. Switchable per session too."
          >
            <span className="text-muted-foreground">Unattended view by default</span>
            <button
              type="button"
              role="switch"
              aria-checked={rcUnattendedDefault}
              onClick={toggleRcUnattendedDefault}
              className={`relative h-5 w-9 rounded-full transition-colors ${rcUnattendedDefault ? 'bg-primary' : 'bg-muted'}`}
            >
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${rcUnattendedDefault ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </label>
          <div className={`flex items-center gap-1.5 text-sm font-medium ${connected ? "text-green-500" : "text-red-500"}`}>
            <div className={`w-2 h-2 rounded-full ${connected ? "bg-green-500 animate-pulse" : "bg-red-500"}`}></div>
            {connected ? "Connected" : "Disconnected"}
          </div>
        </div>
      </div>

      {Object.keys(groupedBooths).length === 0 && unattachedDevices.length === 0 ? (
        <div className="bg-card rounded-xl p-12 shadow-sm border text-center flex flex-col items-center justify-center space-y-3">
          <Monitor className="h-12 w-12 text-muted-foreground/30" />
          <h3 className="text-lg font-semibold text-muted-foreground">No Active Booths Detected</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            When a photobooth client comes online and connects to an event, it will automatically appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedBooths).map(([eventId, booths]) => {
            const eventName = events.find(e => e.id === eventId)?.name || 'Unknown Event'
            
            return (
              <div key={eventId} className="bg-card rounded-xl p-5 shadow-sm border space-y-4">
                <div className="flex items-center gap-2 border-b border-border/50 pb-3">
                  <h2 className="text-lg font-bold">{eventName}</h2>
                  <span className="bg-muted text-muted-foreground text-[10px] uppercase font-bold px-2 py-0.5 rounded-full">
                    {booths.length} online
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {booths.map(booth => {
                    const device = devices[booth.deviceId]
                    // The agent is authoritative on version, it reads it from
                    // the main process. The booth page's own report is the
                    // fallback for clients that predate the agent.
                    const version = device?.stats?.appVersion || booth.appVersion || null
                    const hasAgent = !!device?.online

                    return (
                      <div
                        key={booth.deviceId}
                        onClick={() => setOpenDevice(booth.deviceId)}
                        className="flex cursor-pointer items-center justify-between rounded-xl border border-border/50 bg-muted/20 p-4 transition-colors hover:border-primary/40 hover:bg-muted/40"
                      >
                        <div className="min-w-0 space-y-1.5">
                          <div className="flex items-center gap-2 text-sm font-bold">
                            <div className="h-2 w-2 rounded-full bg-green-500"></div>
                            Booth {booth.deviceId.substring(0, 4)}
                            {version && (
                              <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground">
                                v{version}
                              </span>
                            )}
                            {hasAgent && (
                              <span title="Device agent connected" className="text-primary">
                                <Cpu size={12} />
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            Screen: <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">{booth.screen}</span>
                            {booth.screen === 'capture' && <span className="font-medium text-primary">• Pose: {booth.currentPose}</span>}
                          </div>
                          <div className="text-[10px] text-muted-foreground/70">
                            {hasAgent ? 'Click for files, resources and camera' : 'No agent, page reporting only'}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {hasAgent && (
                            <button
                              onClick={(e) => { e.stopPropagation(); startRemoteControl(booth.deviceId) }}
                              disabled={rcPending === booth.deviceId}
                              className="rounded-lg border border-primary/20 bg-primary/10 p-2.5 text-primary transition-colors hover:bg-primary/20 disabled:opacity-50"
                              title={rcUnattendedDefault ? "Start unattended view" : "Take over screen"}
                            >
                              <ScreenShare size={16} />
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); sendCommand('RELOAD_KIOSK', { target: booth.deviceId, targetEventId: eventId }) }}
                            className="rounded-lg bg-secondary p-2.5 text-secondary-foreground transition-colors hover:bg-secondary/80"
                            title="Reload Page"
                          >
                            <RefreshCw size={16} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); sendCommand('RESTART_EVENT', { target: booth.deviceId, targetEventId: eventId }) }}
                            className="rounded-lg border border-destructive/20 bg-destructive/10 p-2.5 text-destructive transition-colors hover:bg-destructive/20"
                            title="Restart to Welcome Screen"
                          >
                            <Square size={16} />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}

          {/* Machines reachable through the agent with no booth page open.
              This is the case the agent exists for: a kiosk sitting on the hub,
              showing a white screen, or pointed at the wrong URL is invisible to
              booth-status reporting but still fully manageable from here. */}
          {unattachedDevices.length > 0 && (
            <div className="space-y-4 rounded-xl border bg-card p-5 shadow-sm">
              <div className="flex items-center gap-2 border-b border-border/50 pb-3">
                <h2 className="text-lg font-bold">Machines without an open booth</h2>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">
                  {unattachedDevices.length} online
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {unattachedDevices.map((device) => (
                  <div
                    key={device.deviceId}
                    onClick={() => setOpenDevice(device.deviceId)}
                    className="flex cursor-pointer items-center justify-between rounded-xl border border-border/50 bg-muted/20 p-4 transition-colors hover:border-primary/40 hover:bg-muted/40"
                  >
                    <div className="min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 text-sm font-bold">
                        <Cpu size={13} className="text-primary" />
                        {device.stats?.hostname || `Device ${device.deviceId.substring(0, 4)}`}
                        {device.stats?.appVersion && (
                          <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground">
                            v{device.stats.appVersion}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground/70">
                        Agent connected · no booth page open
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); startRemoteControl(device.deviceId) }}
                      disabled={rcPending === device.deviceId}
                      className="shrink-0 rounded-lg border border-primary/20 bg-primary/10 p-2.5 text-primary transition-colors hover:bg-primary/20 disabled:opacity-50"
                      title={rcUnattendedDefault ? "Start unattended view" : "Take over screen"}
                    >
                      <ScreenShare size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {rcError && (
        <div className="fixed bottom-4 right-4 z-[190] flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-700 shadow-lg dark:text-amber-400">
          {rcError}
          <button onClick={() => setRcError(null)} className="text-amber-600/70 hover:text-amber-600" aria-label="Dismiss"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {rc && (
        <RemoteControlViewer
          deviceId={rc.deviceId}
          label={devices[rc.deviceId]?.stats?.hostname || `Booth ${rc.deviceId.substring(0, 4)}`}
          sessionId={rc.sessionId}
          iceServers={rc.iceServers}
          sendSignal={sendRcSignal}
          onInboundRef={rcInboundRef}
          onClose={closeRemoteControl}
          initialUnattended={rc.unattended}
        />
      )}

      {openDevice && (
        <DevicePanel
          deviceId={openDevice}
          boothLabel={
            devices[openDevice]?.stats?.hostname || `Booth ${openDevice.substring(0, 4)}`
          }
          online={!!devices[openDevice]?.online}
          stats={devices[openDevice]?.stats ?? null}
          request={request}
          onClose={() => setOpenDevice(null)}
        />
      )}
    </div>
  )
}
